import MetaRobots from 'components/Basic/Head/MetaRobots';
import SeoMeta from 'components/Basic/Head/SeoMeta';
import { getEndCursor } from 'components/Blocks/Pagination/paginationHelpers';
import SearchPage from 'components/Pages/Search';
import { useSearch } from 'connectors/search/Search';
import {
    SearchProductsQueryDocumentApi,
    SearchProductsQueryVariablesApi,
    SearchQueryDocumentApi,
    SearchQueryVariablesApi,
} from 'graphql/generated';
import { getMappedProductFilter } from 'helpers/filterOptions/getMappedProductFilter';
import { mapParametersFilter } from 'helpers/filterOptions/MapParametersFilter';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getProductListSortFromUrlQuery } from 'helpers/parsing/urlParsing';
import {
    FILTER_QUERY_PARAMETER_NAME,
    PAGE_QUERY_PARAMETER_NAME,
    SEARCH_QUERY_PARAMETER_NAME,
    SORT_QUERY_PARAMETER_NAME,
} from 'helpers/queryParams/queryParamNames';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useCurrentSearchStringQuery } from 'helpers/queryParams/useCurrentSearchStringQuery';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { useGtmStaticPageView } from 'hooks/gtm/useGtmStaticPageView';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { NextPageExtended } from 'types/global';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';
import { getNumberFromUrlQuery } from 'utils/getNumberFromUrlQuery';
import { getStringFromUrlQuery } from 'utils/getStringFromUrlQuery';
import { useGtmStaticPageViewEvent } from 'utils/Gtm/EventFactories';

const Search: NextPageExtended<ServerSidePropsType> = () => {
    const t = useTypedTranslationFunction();
    const router = useRouter();
    const { url: domainUrl } = useDomainConfig();
    const searchProductsSort = getProductListSortFromUrlQuery(router.query[SORT_QUERY_PARAMETER_NAME]);
    const currentFilter = useCurrentFilterQuery();

    const searchParametersFilter = mapParametersFilter(currentFilter);
    const currentSearchString = useCurrentSearchStringQuery();
    const { searchResults } = useSearch(currentSearchString, searchProductsSort, searchParametersFilter);

    const [searchUrl] = getInternationalizedStaticUrls(['/search'], domainUrl);
    const breadcrumbs = useMemo(() => [{ name: t('Search'), slug: searchUrl }], [t, searchUrl]);
    const gtmStaticPageViewEvent = useGtmStaticPageViewEvent('search', breadcrumbs);
    useGtmStaticPageView(gtmStaticPageViewEvent);

    return (
        <>
            <MetaRobots content="noindex, nofollow" />
            <SeoMeta title={t('Search')} />
            <SearchPage breadcrumbs={breadcrumbs} searchResults={searchResults} />
        </>
    );
};

Search.layout = 'withLastVisitedProducts';

export const getServerSideProps = getServerSidePropsWrapper(({ domainConfig, redisClient, t }) => async (context) => {
    const orderingMode = getProductListSortFromUrlQuery(context.query[SORT_QUERY_PARAMETER_NAME]);
    const filter = getMappedProductFilter(context.query[FILTER_QUERY_PARAMETER_NAME]);
    const page = getNumberFromUrlQuery(context.query[PAGE_QUERY_PARAMETER_NAME], 1);
    const search = getStringFromUrlQuery(context.query[SEARCH_QUERY_PARAMETER_NAME]);

    return initServerSideProps<SearchQueryVariablesApi | SearchProductsQueryVariablesApi>({
        context,
        domainConfig,
        prefetchedQueries: [
            {
                query: SearchQueryDocumentApi,
                variables: {
                    search,
                    orderingMode,
                    filter,
                },
            },
            {
                query: SearchProductsQueryDocumentApi,
                variables: {
                    search,
                    orderingMode,
                    filter,
                    endCursor: getEndCursor(page),
                },
            },
        ],
        redisClient,
        t,
    });
});

export default Search;
